//
// Type-aware numeric parsing/validation for the numeric entityInputFields types
// (numberTextField, currencyTextField, percentageTextField).
//
// These fields enforce numeric input only at keypress, so pasted text bypasses the
// gate. The helpers below give a single canonical "is this a clean number for this
// field type" notion, shared by the form validator, the blur-format guard, and the
// save-time converter so they can never disagree.
//
// Decoration is type-aware: commas and whitespace are always tolerated (they are the
// app's own blurred-display formatting), but a '$' is only acceptable in a
// currencyTextField and a '%' only in a percentageTextField. A plain numberTextField
// accepts neither — so '$123,123.00' pasted into '# Employees' is invalid, while the
// same paste into a currency field cleans to 123123.
//

// Decoration stripped before the numeric check, keyed by field type. Commas and
// whitespace are common to all; the currency/percentage symbols are type-specific.
const DECORATION_STRIP: Record<string, RegExp> = {
  numberTextField: /[,\s]/g,
  currencyTextField: /[$,\s]/g,
  percentageTextField: /[%,\s]/g,
}

// Human noun used in the validation message per field type.
const NUMERIC_NOUN: Record<string, string> = {
  numberTextField: 'number',
  currencyTextField: 'dollar amount',
  percentageTextField: 'percentage',
}

// After stripping the type's allowed decoration the remainder must be a plain number:
// digits with at most one decimal point and an optional leading minus.
const CLEAN_NUMBER = /^-?(\d+(\.\d*)?|\.\d+)$/

// Intl 'compact' (en-US) suffixes and their multipliers. Only currency renders
// compact (formatCurrencyAmount uses notation:'compact' for multiples of 100, so an
// existing $1,500 shows as '$1.50K' on blur), so suffix expansion is scoped to
// currencyTextField below. Expand (×), never strip — '5K' is 5000, not 5.
const COMPACT_MULTIPLIER: Record<string, number> = {
  K: 1e3,
  M: 1e6,
  B: 1e9,
  T: 1e12,
}

// A plain number followed by a single compact suffix, e.g. '1.50K' / '-5M'.
const COMPACT_NUMBER = /^(-?(?:\d+(?:\.\d*)?|\.\d+))([KMBT])$/i

export function isNumericFieldType(type: string): boolean {
  return (
    type === 'numberTextField' ||
    type === 'currencyTextField' ||
    type === 'percentageTextField'
  )
}

//
// Returns the clean number for the given field type, or null when the value is not
// cleanly numeric for that type. Empty/blank is treated as "no number" (null) — the
// caller decides whether that is allowed (see validateNumericField).
//
export function parseNumericFieldValue(
  value: unknown,
  type: string
): number | null {
  const strip = DECORATION_STRIP[type]
  if (!strip) {
    return null
  }
  if (value === null || value === undefined || value === '') {
    return null
  }
  const stripped = String(value).replace(strip, '')
  // Currency is shown in compact notation on blur (e.g. '$1.50K' for 1500), so the
  // blurred display the validator sees carries a K/M/B/T suffix. Expand it back to the
  // full number so the display still validates and round-trips. Currency-only, since
  // it is the only type that renders compact.
  if (type === 'currencyTextField') {
    const compact = stripped.match(COMPACT_NUMBER)
    if (compact) {
      const base = Number(compact[1])
      const mult = COMPACT_MULTIPLIER[compact[2].toUpperCase()]
      if (Number.isFinite(base) && mult) {
        return base * mult
      }
    }
  }
  if (!CLEAN_NUMBER.test(stripped)) {
    return null
  }
  const num = Number(stripped)
  return Number.isFinite(num) ? num : null
}

//
// Vuetify rule: empty passes (use a separate 'required' rule for presence); otherwise
// the value must be a clean number for the field type. Returns true or an error string.
//
export function validateNumericField(
  value: unknown,
  type: string,
  label: string
): true | string {
  if (value === null || value === undefined || value === '') {
    return true
  }
  if (parseNumericFieldValue(value, type) !== null) {
    return true
  }
  return `${label} must be a valid ${NUMERIC_NOUN[type] || 'number'}.`
}
